import React, { useState } from 'react';
import { X, Info, Hash } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CreatePOModal = ({ isOpen, onClose, prData, onComplete }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [ponum, setPonum] = useState('');
  const [description, setDescription] = useState(prData?.description || '');
  const [loading, setLoading] = useState(false);

  // دالة توليد رقم تلقائي (Autonumber)
  const handleAutonumber = async () => {
    try {
      const res = await axios.get(`/api/po/next-number/${prData.orgid}`);
      setPonum(res.data.next_ponum);
    } catch (err) { toast.error(t('createPOModal.autonumberFailed')); }
  };

  const handleConfirm = async () => {
    if (!ponum) return toast.error(t('createPOModal.enterOrGenerate'));
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/pr/generate-po', {
        prnum: prData.prnum,
        ponum: ponum,
        orgid: prData.orgid
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('createPOModal.createSuccess', { ponum }));
      onComplete(ponum); // نبعت الرقم عشان نعمل navigate
    } catch (err) {
      toast.error(err.response?.data?.detail || t('createPOModal.generationFailed'));
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-[600px] shadow-2xl border border-line rounded-sm overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-surface-panel p-3 border-b border-line flex justify-between items-center">
          <span className="font-bold text-ink text-sm uppercase">{t('createPOModal.title')}</span>
          <button onClick={onClose} className="hover:text-status-danger"><X size={18}/></button>
        </div>

        <div className="p-6">
          {/* Blue Info Box - زي ماكسيمو بالظبط */}
          <div className="flex gap-4 p-4 bg-status-info-bg border border-status-info-bg rounded-sm mb-6">
            <Info className="text-status-info shrink-0" size={24} />
            <p className="text-[12px] text-status-info leading-relaxed">
              {t('createPOModal.infoText')}
            </p>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            <div className="flex flex-col gap-1 group">
              <label className="text-[11px] font-bold text-ink-secondary uppercase tracking-tighter">{t('createPOModal.poLabel')}</label>
              <input
                autoFocus
                className="border-b-2 border-primary outline-none p-1 font-black text-primary w-32 focus:border-accent transition-all"
                value={ponum}
                onChange={(e) => setPonum(e.target.value.toUpperCase())}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-bold text-ink-secondary uppercase tracking-tighter">{t('createPOModal.descriptionLabel')}</label>
              <input
                className="border-b border-line outline-none p-1 font-bold text-ink w-full"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="bg-surface-panel p-4 border-t border-line flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-1.5 border border-primary text-primary font-bold hover:bg-primary-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={handleConfirm} disabled={loading} className="px-8 py-1.5 bg-primary text-ink-inverse font-bold hover:bg-primary-hover disabled:opacity-50">{t('common.ok', 'OK')}</button>
          <button onClick={handleAutonumber} className="px-6 py-1.5 border border-primary text-primary font-bold hover:bg-primary-soft">{t('createPOModal.autonumber')}</button>
        </div>
      </div>
    </div>
  );
};

export default CreatePOModal;
